import { useMemo, useState } from "react";
import { Download, Loader2 } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { AdminAppRow, ListRow, useTopShopApplications } from "./useTopShopAdmin";
import { useTopShopChallengeMatrix } from "./useTopShopChallengeAdmin";
import { TopShopApplicationSheet } from "./TopShopApplicationSheet";

const ALL = "__all";
function fmt(n: number) { return Number.isInteger(n) ? String(n) : n.toFixed(1); }

export function TopShopChallengesTab({ programId, lists }: { programId: string; lists?: { focusAreas: ListRow[] } }) {
  const { data: apps, isLoading } = useTopShopApplications(programId);
  const { data: matrix, isLoading: mLoading } = useTopShopChallengeMatrix(programId);
  const [community, setCommunity] = useState(ALL);
  const [needsScoring, setNeedsScoring] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const accepted = useMemo(() => (apps ?? []).filter((a) => (a.status === "accepted" || a.status === "completed"))
    .sort((a, b) => (a.full_name ?? "").localeCompare(b.full_name ?? "")), [apps]);
  const communities = useMemo(() => Array.from(new Set(accepted.map((a) => a.community_name).filter(Boolean))) as string[], [accepted]);

  const rows = accepted.filter((a) => {
    if (community === "__unassigned") { if (a.community_id) return false; }
    else if (community !== ALL && a.community_name !== community) return false;
    if (needsScoring) {
      const cells = matrix?.cells[a.id] ?? {};
      return Object.values(cells).some((c) => c.status !== "none" && c.score === null);
    }
    return true;
  });

  const rowTotal = (a: AdminAppRow) => Object.values(matrix?.cells[a.id] ?? {}).reduce((s, c) => s + (c.score ?? 0), 0);

  const cellText = (appId: string, areaId: string) => {
    const c = matrix?.cells[appId]?.[areaId];
    if (!c || c.status === "none") return "-";
    const label = c.status === "reviewed" ? "Reviewed" : "Submitted";
    return c.score !== null ? `${label} (${fmt(c.score)})` : label;
  };

  const exportCsv = () => {
    const cols = matrix?.columns ?? [];
    const header = ["Name", "Business", "Community", ...cols.map((c) => `${c.number} ${c.title}`), "Total"];
    const esc = (v: string) => `"${v.replace(/"/g, '""')}"`;
    const lines = [header, ...rows.map((a) => [a.full_name ?? "", a.business_name, a.community_name ?? "",
      ...cols.map((c) => cellText(a.id, c.focusAreaId)), fmt(rowTotal(a))])].map((r) => r.map((v) => esc(String(v))).join(","));
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `top-shop-challenges-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  if (isLoading || mLoading) return <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  const selected = (apps ?? []).find((a) => a.id === selectedId) ?? null;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <Select value={community} onValueChange={setCommunity}>
          <SelectTrigger className="w-[200px]" aria-label="Community"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>All communities</SelectItem>
            {communities.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            <SelectItem value="__unassigned">Not assigned</SelectItem>
          </SelectContent>
        </Select>
        <div className="flex items-center gap-2">
          <Checkbox id="needs-scoring" checked={needsScoring} onCheckedChange={(v) => setNeedsScoring(!!v)} />
          <Label htmlFor="needs-scoring">Needs scoring</Label>
        </div>
        <Button variant="outline" className="ml-auto" onClick={exportCsv} disabled={!rows.length}>
          <Download className="mr-2 h-4 w-4" />Export CSV
        </Button>
      </div>
      <Card>
        <CardContent className="overflow-x-auto p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead><TableHead>Business</TableHead><TableHead>Community</TableHead>
                {(matrix?.columns ?? []).map((c) => <TableHead key={c.focusAreaId} className="whitespace-nowrap" title={c.name}>{c.number} {c.title}</TableHead>)}
                <TableHead className="text-right">Total</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.length === 0 && (
                <TableRow><TableCell colSpan={4 + (matrix?.columns.length ?? 0)} className="py-8 text-center text-muted-foreground">No accepted participants match.</TableCell></TableRow>
              )}
              {rows.map((a) => (
                <TableRow key={a.id} className="cursor-pointer" onClick={() => setSelectedId(a.id)}>
                  <TableCell className="font-medium">{a.full_name ?? "-"}</TableCell>
                  <TableCell>{a.business_name}</TableCell>
                  <TableCell>{a.community_name ?? "-"}</TableCell>
                  {(matrix?.columns ?? []).map((c) => <TableCell key={c.focusAreaId} className="whitespace-nowrap text-sm">{cellText(a.id, c.focusAreaId)}</TableCell>)}
                  <TableCell className="text-right font-semibold tabular-nums">{fmt(rowTotal(a))} / {matrix?.activeMax ?? 0}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
      <TopShopApplicationSheet application={selected} focusAreas={lists?.focusAreas ?? []} onClose={() => setSelectedId(null)} />
    </div>
  );
}
